"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Funnel, LabelList, FunnelChart as RechartsFunnelChart, ResponsiveContainer, Tooltip } from "recharts";
import React from "react";
import { AvailableChartColors, type AvailableChartColorsKeys, constructCategoryColors } from "@/lib/chart-utils";
import { cx } from "@/lib/tailwind-utils";

// Hardcoded hex palette — CSS vars don't work inside recharts SVG
const PALETTE: Record<string, string> = {
  blue: "#3b82f6",
  emerald: "#10b981",
  violet: "#8b5cf6",
  amber: "#f59e0b",
  cyan: "#06b6d4",
  pink: "#ec4899",
  lime: "#84cc16",
  fuchsia: "#d946ef",
  gray: "#6b7280"
};
function getHex(colorKey: AvailableChartColorsKeys): string {
  return PALETTE[colorKey] ?? PALETTE["blue"]!;
}
interface FunnelTooltipProps {
  active?: boolean;
  payload?: any[];
  valueFormatter?: (v: number) => string;
}
const FunnelTooltip = ({
  active,
  payload,
  valueFormatter = v => v.toLocaleString()
}: FunnelTooltipProps) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  return <div className="rounded-md border border-border bg-background/90 px-3 py-2 text-body shadow-md backdrop-blur-2xl">
      <p className="font-medium text-foreground">{item.name}</p>
      <p className="tabular-nums text-foreground-secondary">
        {valueFormatter(item.value)}
      </p>
    </div>;
};
export interface FunnelChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  /** Column to use as the label/category for each funnel segment */
  index: string;
  /** Column to use as the numeric value for each funnel segment */
  category: string;
  valueFormatter?: (value: number) => string;
  colors?: AvailableChartColorsKeys[];
  /** Use a single-color gradient instead of cycling distinct colors. Useful for sequential funnels. */
  gradient?: boolean;
}
const FunnelChart = React.forwardRef<HTMLDivElement, FunnelChartProps>(({
  data,
  index,
  category,
  valueFormatter = v => v.toLocaleString(),
  colors = AvailableChartColors,
  gradient = false,
  className,
  ...other
}, forwardedRef) => {
  // Transform incoming data into the recharts funnel format { name, value, fill }
  const labels = data.map(row => String(row[index] ?? ""));
  const categoryColors = constructCategoryColors(labels, colors);
  const baseColor = getHex(colors[0] ?? "blue");
  const funnelData = data.map((row_0, i) => {
    const name = String(row_0[index] ?? "");
    const value = typeof row_0[category] === "number" ? row_0[category] as number : Number(row_0[category]) || 0;
    let fill: string;
    if (gradient) {
      // Progressively lighten the base color using hex alpha
      const t = data.length > 1 ? i / (data.length - 1) : 0;
      const alpha = Math.round((1 - t * 0.6) * 255); // 255 → 102
      fill = `${baseColor}${alpha.toString(16).padStart(2, "0")}`;
    } else {
      const colorKey = categoryColors.get(name) ?? colors[0] ?? "blue";
      fill = getHex(colorKey);
    }
    return {
      name,
      value,
      fill
    };
  });
  return <div ref={forwardedRef} className={cx("h-80 w-full", className)} tremor-id="tremor-raw" {...other}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsFunnelChart>
            <Tooltip content={({
          active,
          payload
        }) => <FunnelTooltip active={active} payload={payload ? [...payload] : []} valueFormatter={valueFormatter} />} />
            <Funnel dataKey="value" data={funnelData} isAnimationActive>
              <LabelList position="center" content={({
            x,
            y,
            width,
            height,
            value: value_0,
            index: i_0
          }: any) => {
            const entry = funnelData[i_0];
            if (!entry || !width || height as number < 20) return null;
            const cx = (x as number) + (width as number) / 2;
            const cy = (y as number) + (height as number) / 2;
            return <g>
                      <text x={cx} y={cy - 7} textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize={12} fontWeight={500}>
                        {entry.name}
                      </text>
                      <text x={cx} y={cy + 9} textAnchor="middle" dominantBaseline="middle" fill="#ffffffcc" fontSize={11}>
                        {valueFormatter(value_0 as number)}
                      </text>
                    </g>;
          }} />
            </Funnel>
          </RechartsFunnelChart>
        </ResponsiveContainer>
      </div>;
});
FunnelChart.displayName = "FunnelChart";
export { FunnelChart };